import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { BentoSkeleton } from '../components/SkeletonLoader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AsymmetricCard — single project card. The asymmetry (column span,
 * aspect, vertical offset) is owned by the parent grid via class props.
 */
const AsymmetricCard = ({ project, index, className = '', aspect = 'aspect-[4/5]', offsetY = 0, kicker }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{ transform: `translateY(${offsetY}px)` }}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.23, 1, 0.32, 1] }}
    >
      <Link
        to={`/work/${project.id}`}
        className={`block relative overflow-hidden group ${aspect}`}
        style={{ backgroundColor: 'var(--bg-secondary)' }}
        data-testid={`project-card-${project.id}`}
        data-cursor-hover
      >
        {/* Kicker number — luxury editorial cue */}
        {kicker && (
          <span
            className="absolute top-5 left-5 z-10 text-xs uppercase tracking-[0.3em] px-2.5 py-1 transition-opacity duration-500 opacity-0 group-hover:opacity-100"
            style={{
              color: '#C5A55A',
              backgroundColor: 'rgba(8,8,8,0.55)',
              backdropFilter: 'blur(10px)',
              fontFamily: 'Nohemi, sans-serif',
            }}
          >
            {kicker}
          </span>
        )}

        <img
          src={project.image_url || 'https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?w=800'}
          alt={project.title}
          className="w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.04]"
          loading="lazy"
        />

        {/* Bottom info layer */}
        <div
          className="absolute inset-x-0 bottom-0 p-5 md:p-7 transition-all duration-500"
          style={{
            background:
              'linear-gradient(to top, rgba(8,8,8,0.85) 0%, rgba(8,8,8,0.35) 55%, transparent 100%)',
          }}
        >
          <p
            className="text-[10px] md:text-xs uppercase tracking-[0.25em] mb-1.5"
            style={{ color: '#C5A55A', fontFamily: 'Nohemi, sans-serif' }}
          >
            {project.client}
          </p>
          <h3
            className="text-base md:text-2xl font-bold text-white leading-tight"
            style={{ fontFamily: 'Nohemi, sans-serif', letterSpacing: '-0.01em' }}
          >
            {project.title}
          </h3>
        </div>
      </Link>
    </motion.div>
  );
};

const SectionHeading = ({ eyebrow, title, subtitle }) => (
  <div className="mb-12 md:mb-16">
    <p
      className="text-xs uppercase tracking-[0.35em] mb-3"
      style={{ color: '#C5A55A', fontFamily: 'Nohemi, sans-serif' }}
    >
      {eyebrow}
    </p>
    <h2
      className="font-bold"
      style={{
        fontSize: 'clamp(2rem, 5vw, 4.5rem)',
        lineHeight: '1.0',
        letterSpacing: '-0.03em',
        color: 'var(--text-primary)',
        fontFamily: 'Nohemi, sans-serif',
      }}
    >
      {title}
    </h2>
    {subtitle && (
      <p
        className="mt-4 max-w-2xl text-sm md:text-base"
        style={{ color: 'var(--text-secondary)' }}
      >
        {subtitle}
      </p>
    )}
  </div>
);

const Work = () => {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [counts, setCounts] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data);
        const cats = ['All', ...new Set(response.data.map((p) => p.category))];
        setCategories(cats);
        const c = { All: response.data.length };
        for (const p of response.data) c[p.category] = (c[p.category] || 0) + 1;
        setCounts(c);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <BentoSkeleton />
        </div>
      </div>
    );
  }

  const allCategoryActive = selectedCategory === 'All';
  const filtered =
    allCategoryActive ? projects : projects.filter((p) => p.category === selectedCategory);

  const featured = allCategoryActive ? filtered.filter((p) => p.featured) : [];
  const rest = allCategoryActive ? filtered.filter((p) => !p.featured) : filtered;

  return (
    <div className="min-h-screen pt-32 pb-24 px-6" data-testid="work-page">
      <div className="max-w-7xl mx-auto">
        {/* Hero */}
        <div className="mb-24 md:mb-32">
          <motion.h1
            className="font-black mb-6"
            style={{
              fontSize: 'clamp(3.5rem, 8vw, 8rem)',
              lineHeight: '0.95',
              fontWeight: 900,
              letterSpacing: '-0.03em',
              color: 'var(--text-primary)',
              fontFamily: 'Nohemi, sans-serif',
            }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
          >
            Work
          </motion.h1>
          <motion.p
            className="text-lg max-w-3xl"
            style={{ color: 'var(--text-secondary)' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.23, 1, 0.32, 1] }}
          >
            A selection of campaigns and projects across industries, built to move businesses forward.
          </motion.p>
        </div>

        {/* Category Filter — pill with count */}
        <motion.div
          className="mb-20 flex flex-wrap gap-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.25 }}
        >
          {categories.map((category) => {
            const active = selectedCategory === category;
            return (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className="px-5 py-2 text-sm transition-all rounded-full flex items-center gap-2"
                style={{
                  backgroundColor: active ? 'var(--text-primary)' : 'transparent',
                  color: active ? 'var(--bg-primary)' : 'var(--text-primary)',
                  border: `1px solid ${active ? 'var(--text-primary)' : 'var(--border-strong)'}`,
                  fontFamily: 'Nohemi, sans-serif',
                }}
                data-testid={`category-filter-${category}`}
                data-cursor-hover
              >
                <span>{category}</span>
                <span
                  className="text-[10px] tabular-nums"
                  style={{ color: active ? 'var(--bg-primary)' : '#C5A55A' }}
                >
                  {String(counts[category] || 0).padStart(2, '0')}
                </span>
              </button>
            );
          })}
        </motion.div>

        {/* SELECTED WORK — Asymmetric featured rail (only when All) */}
        {allCategoryActive && featured.length === 4 && (
          <section className="mb-32 md:mb-40" data-testid="selected-work">
            <SectionHeading eyebrow="Selected Work" title="Hand-picked." />

            <div className="grid grid-cols-12 gap-5 md:gap-8">
              {/* Card 1 — wide landscape */}
              <div className="col-span-12 md:col-span-7">
                <AsymmetricCard
                  project={featured[0]}
                  index={0}
                  aspect="aspect-[16/10]"
                  kicker="01 / Featured"
                />
              </div>

              {/* Card 2 — portrait, offset DOWN */}
              <div className="col-span-12 md:col-span-5 md:mt-20">
                <AsymmetricCard
                  project={featured[1]}
                  index={1}
                  aspect="aspect-[4/5]"
                  kicker="02 / Featured"
                />
              </div>

              {/* Card 3 — portrait */}
              <div className="col-span-12 md:col-span-5 md:-mt-10">
                <AsymmetricCard
                  project={featured[2]}
                  index={2}
                  aspect="aspect-[4/5]"
                  kicker="03 / Featured"
                />
              </div>

              {/* Card 4 — wide landscape, offset UP */}
              <div className="col-span-12 md:col-span-7 md:mt-10">
                <AsymmetricCard
                  project={featured[3]}
                  index={3}
                  aspect="aspect-[16/10]"
                  kicker="04 / Featured"
                />
              </div>
            </div>
          </section>
        )}

        {/* ALL WORK — Asymmetric staggered grid */}
        {rest.length > 0 && (
          <section data-testid="all-work">
            {allCategoryActive && (
              <SectionHeading
                eyebrow="The Full Index"
                title="All Work."
              />
            )}

            <div className="grid grid-cols-12 gap-5 md:gap-8">
              {rest.map((project, i) => {
                // Pattern of 4: [7 wide / 5 narrow / 5 narrow / 7 wide],
                // with alternating vertical offsets for the staggered feel.
                const slot = i % 4;
                let cls = '';
                let aspect = '';
                if (slot === 0) {
                  cls = 'col-span-12 md:col-span-7';
                  aspect = 'aspect-[16/10]';
                } else if (slot === 1) {
                  cls = 'col-span-12 md:col-span-5 md:mt-16';
                  aspect = 'aspect-[4/5]';
                } else if (slot === 2) {
                  cls = 'col-span-12 md:col-span-5 md:-mt-12';
                  aspect = 'aspect-[4/5]';
                } else {
                  cls = 'col-span-12 md:col-span-7 md:mt-8';
                  aspect = 'aspect-[16/10]';
                }
                return (
                  <div key={project.id} className={cls}>
                    <AsymmetricCard
                      project={project}
                      index={i}
                      aspect={aspect}
                      kicker={String(i + 1 + featured.length).padStart(2, '0')}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {filtered.length === 0 && (
          <div className="text-center py-20">
            <p className="text-lg" style={{ color: 'var(--text-tertiary)' }}>
              No projects found in this category.
            </p>
          </div>
        )}

        {/* Editorial close — agency-style CTA */}
        <div className="mt-40 md:mt-56 max-w-4xl">
          <p
            className="text-xs uppercase tracking-[0.35em] mb-5"
            style={{ color: '#C5A55A', fontFamily: 'Nohemi, sans-serif' }}
          >
            Next
          </p>
          <Link to="/contact" data-cursor-hover className="group inline-block">
            <h3
              className="font-bold transition-opacity duration-500 group-hover:opacity-60"
              style={{
                fontSize: 'clamp(2rem, 6vw, 5rem)',
                lineHeight: '1.0',
                letterSpacing: '-0.03em',
                color: 'var(--text-primary)',
                fontFamily: 'Nohemi, sans-serif',
              }}
            >
              Got a brand worth&nbsp;building?{' '}
              <span style={{ color: '#C5A55A' }} className="inline-block transition-transform duration-500 group-hover:translate-x-3">
                →
              </span>
            </h3>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Work;
